import { PlusOutlined } from '@ant-design/icons';
import { Button, Card, Col, Flex, Form, Input, Row, Table, TablePaginationConfig } from 'antd';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { notifyError, showConfirm } from '../../../store/utils';
import { API } from '../../../api';
import { PagedResultState } from '../../../common/CommonResult';
import queryString from 'query-string';
import dayjs from 'dayjs';
import BranchSelect from '../../../lib/BranchSelect';
import CustomerSelect from '../../../lib/CustomerSelect';

const BranchStaff = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [state, setState] = useState<PagedResultState<any>>({
    loading: true,
    result: {},
  });

  const fetchBranchStaff = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(
        `/member/branch/staff${location.search}`
      );
      setState({
        loading: false,
        result,
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, [location]);

  useEffect(() => {
    if (!location.search) {
      navigate(`/settings/branch/staff?page=1&size=10`);
    }
  }, [location]);

  useEffect(() => {
    if (location.search) {
      fetchBranchStaff();
    }
  }, [location.search]);

  const handleTableChange = useCallback(
    (pagination: TablePaginationConfig) => {
      const params = queryString.parse(location.search);
      const search = {
        ...params,
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      };
      navigate(`/settings/branch/staff?${queryString.stringify(search)}`);
    },
    [location]
  );

  const showDeleteConfirm = useCallback((record: any) => {
    showConfirm({
      title: 'Устгахдаа итгэлтэй байна уу?',
      content: 'Уг үйлдэл нь буцаагдахгүй!. Хэрэглэгч устахыг анхаарна уу',
      onOk() {
        branchStaffDelete(record.no);
      },
      okText: 'Тийм',
      cancelText: 'Үгүй',
    });
  }, []);

  const branchStaffDelete = useCallback(
    async (memberNo: number) => {
      try {
        await API.delete({ apiVersion: 'api' })(`/member/${memberNo}`);
        fetchBranchStaff();
      } catch (error: any) {
        notifyError(error.message);
      }
    },
    [location]
  );

  const onFinish = useCallback(
    (values: any) => {
      const params = {
        ...values,
        page: 1,
        size: 10,
      };
      navigate(`/settings/branch/staff?${queryString.stringify(params)}`);
    },
    [location]
  );

  const columns = [
    {
      title: 'Салбар нэр',
      key: 'name',
      render: (record: any) => <>{(record?.roles || [])?.[0].courierBranch?.name}</>,
    },
    {
      title: 'Нэвтрэх нэр',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Нэр',
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: 'Утас',
      key: 'phone',
      dataIndex: 'phone',
    },
    {
      title: 'Алба',
      key: 'department',
      render: (record: any) => <></>,
    },
    {
      title: 'Эрх',
      key: 'role',
      render: (record: any) => <>{(record?.roles || [])?.[0].role?.name}</>,
    },
    {
      title: 'Бүртгэсэн огноо',
      key: 'createdAt',
      render: (record: any) => dayjs(record.createdAt).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Төлөв',
      key: 'status',
      render: (record: any) => record?.status?.text,
    },
    {
      title: 'Үйлдэл',
      key: 'action',
      width: 100,
      render: (record: any) => (
        <Flex gap={12}>
          <Link to={`/settings/branch/staff/update/${record.no}`}>
            <Button size="small">Засах</Button>
          </Link>
          {record?.status?.code === 'ACTIVE' ? (
            <Button size="small" type="primary" danger onClick={() => showDeleteConfirm(record)}>
              Устгах
            </Button>
          ) : (
            <Button
              size="small"
              type="primary"
              danger
              //onClick={() => showDeleteConfirm(record)}
            >
              Сэргээх
            </Button>
          )}
        </Flex>
      ),
    },
  ];

  return (
    <Card
      title={`Ажилтаны жагсаалт`}
      extra={
        <Link to="/settings/branch/staff/create">
          <Button type="primary" icon={<PlusOutlined />}>
            Ажилтан нэмэх
          </Button>
        </Link>
      }
    >
      <Form onFinish={onFinish}>
        <Row gutter={[25, 25]}>
          <Col span={4}>
            <Form.Item name="query">
              <Input placeholder="Хайх үг бичнэ үү" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="branchNo">
              <BranchSelect showSearch allowClear />
            </Form.Item>
          </Col>
          <Col span={2}>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Хайх
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Table
        size="small"
        loading={state.loading}
        dataSource={state.result?.data || []}
        rowKey={'no'}
        pagination={{
          current: state?.result?.page || 1,
          size: 'small',
          pageSize: state?.result?.size || 10,
          total: state?.result?.total,
        }}
        onChange={handleTableChange}
        columns={columns}
      />
    </Card>
  );
};

export default BranchStaff;
